import type { CommandType, Device } from "./types";
import { type Requirements, versionReason } from "./versions";

export type FieldKind = "text" | "number" | "boolean" | "select" | "artifact" | "json";

export interface CommandField {
  name: string;
  label: string;
  kind: FieldKind;
  required?: boolean;
  placeholder?: string;
  help?: string;
  options?: { value: string; label: string }[];
  /** Value the form starts with. */
  initial?: string | number | boolean;
  min?: number;
  max?: number;
}

export interface CommandDefinition {
  type: CommandType;
  label: string;
  /** What it does, in a sentence. */
  description: string;
  /** Interrupts what the shop window shows, or can't be undone: asks for confirmation. */
  disruptive: boolean;
  /** Shown in the confirmation. */
  warning?: string;
  fields: CommandField[];
  /** How the result is best shown. */
  result?: "image" | "file" | "location" | "config" | "screen_view";
  /** Has its own panel (live screen view), so it is left out of the generic command grids. */
  liveOnly?: boolean;
}

const PACKAGE_HELP = "The app's package name, e.g. com.yourcompany.display";

export const COMMANDS: CommandDefinition[] = [
  {
    type: "screenshot",
    label: "Screenshot",
    description: "Take a picture of what the device is showing.",
    disruptive: false,
    result: "image",
    fields: [
      {
        name: "format",
        label: "Format",
        kind: "select",
        initial: "jpeg",
        options: [
          { value: "jpeg", label: "JPEG (smaller)" },
          { value: "png", label: "PNG (exact)" },
        ],
      },
      { name: "quality", label: "Quality", kind: "number", initial: 80, min: 1, max: 100 },
      {
        name: "max_dimension",
        label: "Longest side, pixels",
        kind: "number",
        initial: 1280,
        min: 64,
        max: 8192,
        help: "Scaled down to this size; leave empty for full resolution.",
      },
    ],
  },
  {
    type: "screen_view",
    label: "Screen view",
    description: "Watch the screen live for a while, frame by frame.",
    disruptive: false,
    result: "screen_view",
    liveOnly: true,
    fields: [
      { name: "duration_seconds", label: "For, seconds", kind: "number", initial: 60, min: 5, max: 600 },
      { name: "interval_seconds", label: "A frame every, seconds", kind: "number", initial: 2, min: 1, max: 30 },
      {
        name: "max_side",
        label: "Longest side, pixels",
        kind: "number",
        initial: 720,
        min: 240,
        max: 1920,
        help: "Smaller frames travel faster.",
      },
      { name: "quality", label: "Quality", kind: "number", initial: 40, min: 10, max: 90 },
      {
        name: "record",
        label: "Keep the frames as files of the command",
        kind: "boolean",
        initial: false,
      },
    ],
  },
  {
    type: "screen_stream",
    label: "HD live",
    description: "Watch the screen as a smooth MediaProjection stream.",
    disruptive: false,
    // Its result is shown exactly like a screen view's (frames + summary player).
    result: "screen_view",
    liveOnly: true,
    fields: [
      { name: "duration_seconds", label: "For, seconds", kind: "number", initial: 300, min: 5, max: 1800 },
      { name: "fps", label: "Frames a second", kind: "number", initial: 6, min: 1, max: 15 },
      {
        name: "max_side",
        label: "Longest side, pixels",
        kind: "number",
        initial: 900,
        min: 240,
        max: 1920,
        help: "Smaller frames travel faster.",
      },
      { name: "quality", label: "Quality", kind: "number", initial: 45, min: 10, max: 90 },
      {
        name: "record",
        label: "Keep the frames as files of the command",
        kind: "boolean",
        initial: false,
      },
    ],
  },
  {
    type: "get_location",
    label: "Locate",
    description: "Ask the device where it is.",
    disruptive: false,
    result: "location",
    fields: [
      { name: "timeout_seconds", label: "Wait up to, seconds", kind: "number", initial: 30, min: 1, max: 300 },
      { name: "high_accuracy", label: "Use GPS (slower, more precise)", kind: "boolean", initial: true },
    ],
  },
  {
    type: "relaunch_kiosk",
    label: "Relaunch kiosk app",
    description: "Bring the kiosk app back to the screen.",
    disruptive: true,
    warning: "The display restarts and is blank for a moment.",
    fields: [
      { name: "force_stop", label: "Stop the app first", kind: "boolean", initial: true },
    ],
  },
  {
    type: "set_kiosk_mode",
    label: "Kiosk mode",
    description: "Lock the device to one app, or release it.",
    disruptive: true,
    warning: "Releasing the lock lets anyone at the device leave the kiosk app.",
    fields: [
      { name: "enabled", label: "Lock the device to the app", kind: "boolean", initial: true },
      {
        name: "package_name",
        label: "App",
        kind: "text",
        placeholder: "com.yourcompany.display",
        help: `${PACKAGE_HELP}. Empty means the app already configured on the device.`,
      },
    ],
  },
  {
    type: "reboot",
    label: "Reboot",
    description: "Restart the device.",
    disruptive: true,
    warning: "The display goes dark for a minute or two.",
    fields: [
      { name: "delay_seconds", label: "Wait first, seconds", kind: "number", initial: 0, min: 0, max: 3600 },
      { name: "reason", label: "Reason (for the log)", kind: "text" },
    ],
  },
  {
    type: "install_apk",
    label: "Install or update app",
    description: "Install an APK that was uploaded to the server.",
    disruptive: true,
    warning: "Updating the kiosk app restarts it.",
    fields: [
      { name: "file_id", label: "APK", kind: "artifact", required: true },
      { name: "package_name", label: "Expected package", kind: "text", help: `${PACKAGE_HELP}. Checked against the APK.` },
      { name: "launch_after_install", label: "Start it afterwards", kind: "boolean", initial: false },
    ],
  },
  {
    type: "uninstall",
    label: "Uninstall app",
    description: "Remove an app from the device.",
    disruptive: true,
    warning: "The app and its data are removed from the device.",
    fields: [
      { name: "package_name", label: "App", kind: "text", required: true, placeholder: "com.example.app", help: PACKAGE_HELP },
    ],
  },
  {
    type: "push_file",
    label: "Send file",
    description: "Put a file that was uploaded to the server onto the device.",
    disruptive: false,
    fields: [
      { name: "file_id", label: "File", kind: "artifact", required: true },
      {
        name: "destination_path",
        label: "Save as",
        kind: "text",
        required: true,
        placeholder: "content/menu.mp4",
        help: "Relative paths start in the device's DroidManage folder.",
      },
      { name: "overwrite", label: "Replace an existing file", kind: "boolean", initial: true },
    ],
  },
  {
    type: "pull_file",
    label: "Fetch file",
    description: "Copy a file from the device to the server.",
    disruptive: false,
    result: "file",
    fields: [
      { name: "path", label: "File on the device", kind: "text", required: true, placeholder: "content/menu.mp4" },
    ],
  },
  {
    type: "pull_logs",
    label: "Fetch logs",
    description: "Collect log lines from the device.",
    disruptive: false,
    result: "file",
    fields: [
      {
        name: "source",
        label: "Which log",
        kind: "select",
        initial: "agent",
        options: [
          { value: "agent", label: "Agent" },
          { value: "kiosk", label: "Kiosk app" },
          { value: "logcat", label: "System (logcat)" },
        ],
      },
      { name: "max_lines", label: "Lines, at most", kind: "number", initial: 2000, min: 1, max: 100000 },
    ],
  },
  {
    type: "update_config",
    label: "Change settings",
    description: "Change the agent's settings on the device.",
    disruptive: false,
    result: "config",
    fields: [
      {
        name: "config",
        label: "Settings (JSON)",
        kind: "json",
        required: true,
        initial: '{\n  "telemetry_interval_seconds": 60\n}',
        help: "Known keys: kiosk_package, kiosk_enabled, telemetry_interval_seconds, location_enabled, keep_screen_on, restrictions.",
      },
      { name: "merge", label: "Keep the settings not mentioned", kind: "boolean", initial: true },
    ],
  },
  {
    type: "audio_capture",
    label: "Record audio",
    description: "Capture the microphone. The device always shows a recording indicator and plays a tone.",
    disruptive: true,
    warning:
      "The person at the device will see a recording notification, hear a tone, and see Android's microphone indicator — this is never silent, by design. Lawful only on company-owned devices under a signed monitoring policy. Give a reason; it is written to the audit log.",
    result: "file",
    fields: [
      { name: "duration_seconds", label: "For, seconds", kind: "number", initial: 60, min: 5, max: 600 },
      // "stream" (listen live) is supported by the agent and protocol but not yet wired
      // through the server relay + a browser player, so only record mode is offered here.
      {
        name: "sample_rate_hz",
        label: "Quality",
        kind: "select",
        initial: "16000",
        options: [
          { value: "8000", label: "Voice (8 kHz)" },
          { value: "16000", label: "Wideband (16 kHz)" },
          { value: "44100", label: "Full (44.1 kHz)" },
        ],
      },
      { name: "reason", label: "Reason (for the audit log)", kind: "text", required: true },
    ],
  },
  {
    // Driven only by the File Manager, never shown in the Actions grid or Broadcast.
    type: "list_dir",
    label: "List files",
    description: "List a folder on the device.",
    disruptive: false,
    liveOnly: true,
    fields: [],
  },
  {
    type: "delete_file",
    label: "Delete file",
    description: "Delete a file or folder on the device.",
    disruptive: true,
    warning: "The file or folder is removed from the device and can't be recovered.",
    liveOnly: true,
    fields: [],
  },
];

const BY_TYPE = new Map(COMMANDS.map((command) => [command.type as string, command]));

export function commandDefinition(type: string): CommandDefinition | undefined {
  return BY_TYPE.get(type);
}

export function commandLabel(type: string): string {
  return BY_TYPE.get(type)?.label ?? type;
}

/** The values a form for this command starts with. */
export function initialValues(definition: CommandDefinition): Record<string, string | boolean> {
  return Object.fromEntries(
    definition.fields.map((field) => [
      field.name,
      field.kind === "boolean" ? Boolean(field.initial) : String(field.initial ?? ""),
    ]),
  );
}

export type FormErrors = Record<string, string>;

/** Turns what was typed into the command's payload, or says what is wrong. */
export function buildPayload(
  definition: CommandDefinition,
  values: Record<string, string | boolean>,
): { payload: Record<string, unknown>; errors: FormErrors } {
  const payload: Record<string, unknown> = {};
  const errors: FormErrors = {};

  for (const field of definition.fields) {
    const raw = values[field.name];
    if (field.kind === "boolean") {
      payload[field.name] = Boolean(raw);
      continue;
    }
    const text = String(raw ?? "").trim();
    if (!text) {
      if (field.required) errors[field.name] = "Required";
      continue;
    }
    if (field.kind === "number") {
      const value = Number(text);
      if (!Number.isInteger(value)) errors[field.name] = "Enter a whole number";
      else if (field.min !== undefined && value < field.min) errors[field.name] = `At least ${field.min}`;
      else if (field.max !== undefined && value > field.max) errors[field.name] = `At most ${field.max}`;
      else payload[field.name] = value;
    } else if (field.kind === "json") {
      try {
        const value: unknown = JSON.parse(text);
        if (value === null || typeof value !== "object" || Array.isArray(value)) {
          errors[field.name] = "Enter a JSON object, like { \"key\": \"value\" }";
        } else {
          payload[field.name] = value;
        }
      } catch {
        errors[field.name] = "This is not valid JSON";
      }
    } else {
      payload[field.name] = text;
    }
  }
  return { payload, errors };
}

/**
 * A copy of a command's payload or result with on-device PIN values hidden, so a
 * ui_pin is never shown anywhere it would otherwise be printed (command history, results).
 */
export function maskSecrets<T>(value: T): T {
  if (Array.isArray(value)) return value.map((item) => maskSecrets(item)) as T;
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      out[key] = key === "ui_pin" && typeof item === "string" && item !== "" ? "••••" : maskSecrets(item);
    }
    return out as T;
  }
  return value;
}

/** Commands the agent only runs as Device Owner. */
const OWNER_ONLY = new Set(["reboot", "install_apk", "uninstall", "set_kiosk_mode"]);
/** Commands that need the agent's accessibility screenshot service on the phone. */
const SCREENSHOT_BASED = new Set(["screenshot", "screen_view"]);

/** Why the agent doesn't offer a command it has, in words someone at the shop can act on. */
function missingReason(type: string, device: Device): string {
  if (SCREENSHOT_BASED.has(type)) {
    const android = Number.parseInt(device.os_version ?? "", 10);
    if (Number.isFinite(android) && android < 11) return "Screenshots need Android 11 or newer";
    return `The phone's screenshot service is switched off. Turn on "DroidManage screenshots" in its Accessibility settings`;
  }
  if (OWNER_ONLY.has(type) && device.is_device_owner === false) {
    return "The agent on this device is not Device Owner";
  }
  return "The agent on this device can't do this";
}

/**
 * Why a command can't be sent to this device right now, or null if it can. The server
 * applies the same rules (and refuses), so this only explains them before a click.
 * [requirements] are the agent versions commands need, from the fleet settings.
 */
export function unavailableReason(
  definition: CommandDefinition,
  device: Device,
  requirements?: Requirements,
): string | null {
  if (device.status === "disabled") return "This device is disabled";
  const capabilities = device.capabilities;
  // A device that hasn't reported what it can do (it never connected) isn't second-guessed.
  if (capabilities && !capabilities.includes(definition.type)) {
    return missingReason(definition.type, device);
  }
  return versionReason(definition.type, device, requirements);
}
